// EN-4743. The browser OCR engine for passport MRZ reading.
//
// One tesseract.js worker for the whole page, created only when the visitor picks
// Passport. Everything is served from our own origin (public/ocr/, see
// scripts/copy-ocr-assets.mjs), not tesseract.js's default CDN. The model is
// mrz.traineddata (DoubangoTelecom/tesseractMRZ, BSD-3-Clause), trained on the
// OCR-B MRZ font: the standard English model misread the '<' filler as K, S, 5, E.
//
// Nothing here throws into the page. If the engine can't load (a browser without
// the WebAssembly features it needs, or the download failed) the reader reports
// itself unavailable and the capture falls back to today's photo-only flow.
// tesseract.js is imported dynamically, so it's a separate chunk that only
// passport visitors download.
import type { Worker } from 'tesseract.js';
import type { MrzLine } from '../../utils/mrz/mrz';

// The only characters that appear in an MRZ. Spelled out: Tesseract doesn't
// accept ranges like A-Z.
const MRZ_CHARACTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789<';

const assetUrl = (path: string): string => new URL(`/ocr/${path}`, window.location.href).href;

let workerPromise: Promise<Worker | null> | null = null;
let unavailableReason: string | null = null;

const startWorker = async (): Promise<Worker | null> => {
  try {
    if (typeof WebAssembly !== 'object' || typeof Worker !== 'function') {
      unavailableReason = 'no-webassembly-or-workers';
      return null;
    }
    const { createWorker, OEM, PSM } = await import('tesseract.js');
    const worker = await createWorker('mrz', OEM.LSTM_ONLY, {
      workerPath: assetUrl('worker.min.js'),
      corePath: assetUrl('core'),
      langPath: assetUrl('lang'),
      gzip: false,
    });
    await worker.setParameters({
      tessedit_char_whitelist: MRZ_CHARACTERS,
      tessedit_pageseg_mode: PSM.SINGLE_BLOCK,
    });
    unavailableReason = null;
    return worker;
  } catch (err) {
    unavailableReason = err instanceof Error ? err.message : String(err);
    return null;
  }
};

/** Start loading the engine and model. Safe to call more than once. */
export const preloadMrzOcr = (): Promise<Worker | null> => {
  if (!workerPromise) workerPromise = startWorker();
  return workerPromise;
};

/** True once the engine is loaded; false while loading or if it failed. */
export const isMrzOcrReady = async (): Promise<boolean> => (await preloadMrzOcr()) !== null;

/** Why the engine isn't available, for telemetry. Null when it's fine. */
export const mrzOcrUnavailableReason = (): string | null => unavailableReason;

/**
 * Read the text lines in `canvas`. Empty when the engine isn't available or the
 * pass failed; the caller just tries the next frame.
 */
export const recognizeMrzLines = async (canvas: HTMLCanvasElement): Promise<MrzLine[]> => {
  const worker = await preloadMrzOcr();
  if (!worker) return [];
  try {
    const { data } = await worker.recognize(canvas, {}, { blocks: true });
    return (data.blocks ?? [])
      .flatMap((block) => block.paragraphs.flatMap((paragraph) => paragraph.lines))
      .map((line) => ({ text: line.text, boundingBox: { y: line.bbox.y0 } }));
  } catch {
    return [];
  }
};

/** Free the worker when the visitor leaves the ID capture. */
export const terminateMrzOcr = async (): Promise<void> => {
  const pending = workerPromise;
  workerPromise = null;
  const worker = await pending?.catch(() => null);
  await worker?.terminate().catch(() => undefined);
};
